import React, { forwardRef, useRef, useState } from 'react';
import { translate } from '../../locales';
import PropTypes from 'prop-types';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Tooltip from '../Tooltip/Tooltip';
import { uploadSingleFileClasses as classes } from './uploadSingleFileClasses';
import {
  UploadSingleFileRoot,
  UploadTextField,
  UploadButton,
  FileUploadIcon,
  UploadProgressLoader,
  UploadFileInput
} from './UploadSingleFile.style';

const UploadSingleFile = forwardRef(
  (
    {
      accept,
      disabled,
      isLoading,
      placeholder = translate('uploadSingleFile.placeholder'),
      CircularProgressProps,
      onChange,
      onClick: incomingOnClick,
      onKeyDown: incomingOnKeyDown,
      className,
      sx,
      ...rest
    },
    ref
  ) => {
    const fileInputRef = useRef();
    const [fileName, setFileName] = useState('');

    const handleChange = ({ target }) => {
      const file = target.files[0];
      if (file) {
        setFileName(file.name);
      }

      if (onChange) {
        onChange(file, target);
      }
    };

    const removeFile = () => {
      setFileName('');
      fileInputRef.current.value = '';
      onChange(null, null);
    };

    const uploadFile = () => {
      fileInputRef.current.click();
    };

    /**
     * Change the placement in case there's value in the TextField in order to prevent
     * from one Tooltip to render on top of another Tooltip
     */

    const tooltipPlacement = fileName ? 'right' : 'bottom';
    const tooltipTitle = fileName
      ? translate('uploadSingleFile.delete')
      : translate('uploadSingleFile.upload');
    const iconElement = fileName ? (
      <DeleteOutlineIcon fontSize="small" />
    ) : (
      <FileUploadIcon
        fontSize="small"
        disabled={disabled}
        className={classes.uploadIcon}
      />
    );

    return (
      <UploadSingleFileRoot
        className={className}
        sx={sx}
        data-qa-id="upload-single-file__root">
        <UploadTextField
          data-qa-id="upload-single-file__text-field"
          variant="outlined"
          inputProps={{
            // UX request - skip focus on the input element itself
            tabIndex: -1,
            // because we override the inputProps property we need to provide the default data-qa-id
            'data-qa-id': 'text-field-input'
          }}
          InputProps={{
            readOnly: true,
            startAdornment: isLoading && (
              <UploadProgressLoader
                data-qa-id="upload-single-file-circular-progress"
                aria-label="file-circular-progress"
                size={17}
                {...CircularProgressProps}
              />
            ),
            endAdornment: !isLoading && (
              <Tooltip title={tooltipTitle} placement={tooltipPlacement}>
                <span role="tooltip">
                  <UploadButton
                    disabled={disabled}
                    position="end"
                    data-qa-id="upload-single-file-upload-button"
                    aria-label={tooltipTitle}
                    className={classes.uploadButton}
                    onClick={e => {
                      e.stopPropagation();

                      if (incomingOnClick) {
                        incomingOnClick(e);
                      }

                      if (!fileName) {
                        uploadFile();
                      } else {
                        removeFile();
                      }
                    }}
                    onKeyDown={e => e.stopPropagation()}>
                    {iconElement}
                  </UploadButton>
                </span>
              </Tooltip>
            )
          }}
          placeholder={placeholder}
          onClick={e => {
            if (incomingOnClick) {
              incomingOnClick(e);
            }

            uploadFile();
          }}
          onKeyDown={e => {
            if (incomingOnKeyDown) {
              incomingOnKeyDown(e);
            }

            if (e.key === 'Enter') {
              uploadFile();
            } else if (
              (e.key === 'Delete' || e.key === 'Backspace') &&
              fileName
            ) {
              removeFile();
            }
          }}
          value={fileName}
          ref={ref}
          disabled={disabled}
          tabIndex={0}
          {...rest}
        />
        <UploadFileInput
          data-qa-id="upload-single-file__input-file"
          ref={fileInputRef}
          onChange={handleChange}
          className={classes.fileInput}
          disabled={disabled || isLoading}
          type="file"
          accept={accept}
        />
      </UploadSingleFileRoot>
    );
  }
);

UploadSingleFile.propTypes = {
  id: PropTypes.string,
  /** when passing a label id prop is required */
  label: PropTypes.string,
  /**
   * Get the same value as accept attribute of the input file element.
   */
  accept: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  /** ([File](https://developer.mozilla.org/en-US/docs/Web/API/File): object | null, event: object | null) => void */
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  isLoading: PropTypes.bool,
  CircularProgressProps: PropTypes.object
};

UploadSingleFile.classes = classes;
UploadSingleFile.displayName = 'UploadSingleFile';

export default UploadSingleFile;
